import React, { useId } from 'react';

const GREEN = '#1F6B3A';
const WHITE = '#FFFEF8';
const RED = '#CE1126';
const CORD = '#C4A574';
const TILE = 318;
const COPIES = 10;

function hole(cx, cy, r) {
  return `M${cx - r} ${cy} a${r} ${r} 0 1 0 ${r * 2} 0 a${r} ${r} 0 1 0 ${-r * 2} 0`;
}

function scallopedPaper(x, y, w, h) {
  const lobes = 4;
  const r = w / (lobes * 2);
  const base = y + h - r;
  const arcs = Array.from({ length: lobes }, (_, i) => {
    const idx = lobes - 1 - i;
    const ex = x + idx * r * 2;
    return `A${r} ${r} 0 0 1 ${ex} ${base}`;
  }).join(' ');
  return `M${x} ${y} H${x + w} V${base} ${arcs} V${y} Z`;
}

function laceHoles(x, y, w, h) {
  const holes = [];
  for (let i = 0; i < 9; i += 1) {
    holes.push(hole(x + 6.2, y + 14 + i * 7.2, 1.45));
    holes.push(hole(x + w - 6.2, y + 14 + i * 7.2, 1.45));
  }
  for (let i = 1; i < 10; i += 1) {
    holes.push(hole(x + 8 + i * 8, y + 8.5, 1.25));
  }
  const step = w / 8;
  for (let i = 1; i < 8; i += 1) {
    holes.push(hole(x + i * step, y + h - 22, 1.2));
  }
  return holes.join(' ');
}

function sunburst(cx, cy) {
  const rays = [];
  for (let i = 0; i < 8; i += 1) {
    const a = (i * Math.PI) / 4;
    const x2 = cx + Math.cos(a) * 7.5;
    const y2 = cy + Math.sin(a) * 7.5;
    const wx = Math.cos(a + Math.PI / 2) * 1.15;
    const wy = Math.sin(a + Math.PI / 2) * 1.15;
    rays.push(`M${cx} ${cy} L${x2 - wx} ${y2 - wy} L${x2 + wx} ${y2 + wy} Z`);
  }
  return `${rays.join(' ')} ${hole(cx, cy, 1.8)}`;
}

function agaveMotif(cx, cy) {
  const leaves = [];
  for (let i = -5; i <= 5; i += 1) {
    const ang = (i * 15 * Math.PI) / 180;
    const len = 22 - Math.abs(i) * 1.35;
    const tx = cx + Math.sin(ang) * len;
    const ty = cy - Math.cos(ang) * len * 0.9;
    const wx = Math.cos(ang) * 2.05;
    const wy = Math.sin(ang) * 2.05;
    leaves.push(`M${cx} ${cy + 6} L${tx - wx} ${ty + wy} L${tx + wx} ${ty - wy} Z`);
  }
  return `${leaves.join(' ')} ${hole(cx, cy - 16, 2.4)}`;
}

function cactus(cx, baseY) {
  return [
    `M${cx - 3.4} ${baseY} V${baseY - 28} a3.4 3.4 0 0 1 6.8 0 V${baseY} Z`,
    `M${cx - 3.4} ${baseY - 16} h-8 a2.3 2.3 0 0 1 0 -4.6 h8 Z`,
    `M${cx + 3.4} ${baseY - 12} h7.2 a2.1 2.1 0 0 0 0 -4.2 h-7.2 Z`,
  ].join(' ');
}

function wreath(cx, cy, r) {
  const dots = [];
  for (let i = 0; i < 18; i += 1) {
    const a = (i / 18) * Math.PI * 2;
    dots.push(hole(cx + Math.cos(a) * r, cy + Math.sin(a) * r, 1.35));
  }
  for (let i = 0; i < 12; i += 1) {
    const a = (i / 12) * Math.PI * 2 + 0.2;
    dots.push(hole(cx + Math.cos(a) * (r - 5.2), cy + Math.sin(a) * (r - 5.2), 1.05));
  }
  return dots.join(' ');
}

function eagleMotif(cx, cy) {
  return [
    `M${cx} ${cy - 2} l-16 -8 -2.4 4.6 18.4 10 18.4 -10 -2.4 -4.6 Z`,
    `M${cx - 2.2} ${cy - 3} L${cx - 20} ${cy - 18} L${cx - 5.5} ${cy - 5} Z`,
    `M${cx + 2.2} ${cy - 3} L${cx + 20} ${cy - 18} L${cx + 5.5} ${cy - 5} Z`,
    hole(cx, cy - 10, 3.4),
    `M${cx + 3.2} ${cy - 11} q8 -6 11 1 q-6 2 -11 2 z`,
    `M${cx - 6.5} ${cy + 10} a7 4.4 0 1 0 13 0 a7 4.4 0 1 0 -13 0`,
    `M${cx - 1.15} ${cy + 14} h2.3 v9 h-2.3 z`,
    `M${cx - 8} ${cy + 22} h16 v2.4 h-16 z`,
    wreath(cx, cy, 24.5),
  ].join(' ');
}

function guitarMotif(cx, cy) {
  return [
    `M${cx - 11} ${cy + 9} a6.8 5.4 0 1 0 0.02 0 z`,
    `M${cx - 11} ${cy} a4.8 3.9 0 1 0 0.02 0 z`,
    `M${cx - 9} ${cy - 4} L${cx + 16} ${cy - 22} L${cx + 18.4} ${cy - 18.2} L${cx - 7} ${cy - 0.4} Z`,
    `M${cx + 10} ${cy + 11} a6.8 5.4 0 1 0 0.02 0 z`,
    `M${cx + 10} ${cy + 2} a4.8 3.9 0 1 0 0.02 0 z`,
    `M${cx + 8} ${cy - 2} L${cx - 17} ${cy - 20} L${cx - 19.4} ${cy - 16.2} L${cx + 6} ${cy + 1.6} Z`,
    hole(cx - 2, cy - 18, 1.5),
    hole(cx + 12, cy - 14, 1.35),
    `M${cx - 22} ${cy - 6} l10 -3 1.6 4.8 -10 3 z`,
    `M${cx + 12} ${cy - 8} l10 -3 1.6 4.8 -10 3 z`,
  ].join(' ');
}

function Tassel({ x, y, color }) {
  return (
    <g>
      <circle cx={x} cy={y} r="1.55" fill="#F6E7C8" />
      <circle cx={x} cy={y + 3.4} r="1.85" fill={color} />
      <circle cx={x} cy={y + 6.4} r="1.45" fill={color === WHITE ? CORD : WHITE} />
      <path
        d={`M${x} ${y + 8} l-2.8 11.5 M${x} ${y + 8} v12.6 M${x} ${y + 8} l2.8 11.5 M${x} ${y + 8} l-1.4 11.8 M${x} ${y + 8} l1.4 11.8`}
        stroke={color}
        strokeWidth="1.2"
        strokeLinecap="round"
        fill="none"
      />
    </g>
  );
}

function Banner({ x, color, motif, label, maskId }) {
  const w = 90;
  const h = 88;
  const y = 16;
  const paper = scallopedPaper(x, y, w, h);
  const art = motif === 'agave'
    ? `${sunburst(x + 16, y + 20)} ${sunburst(x + 74, y + 20)} ${agaveMotif(x + 45, y + 42)} ${cactus(x + 18, y + 70)} ${cactus(x + 72, y + 70)}`
    : motif === 'eagle'
      ? eagleMotif(x + 45, y + 40)
      : guitarMotif(x + 45, y + 42);

  return (
    <g>
      <mask id={maskId} maskUnits="userSpaceOnUse">
        <path d={paper} fill="white" />
        <path d={laceHoles(x, y, w, h)} fill="black" />
        <path d={art} fill="black" fillRule="evenodd" />
        <text
          x={x + w / 2}
          y={y + h - 14}
          textAnchor="middle"
          fontFamily="Outfit, system-ui, sans-serif"
          fontSize="5.8"
          fontWeight="800"
          fill="black"
        >
          {label}
        </text>
      </mask>
      <path d={paper} fill={color} mask={`url(#${maskId})`} />
    </g>
  );
}

function Tile({ x, uid, copy }) {
  return (
    <g transform={`translate(${x},0)`}>
      <Banner x={6} color={GREEN} motif="agave" label="¡VIVA MÉXICO!" maskId={`${uid}g${copy}`} />
      <Banner x={114} color={WHITE} motif="eagle" label="16 DE SEPTIEMBRE" maskId={`${uid}w${copy}`} />
      <Banner x={222} color={RED} motif="guitar" label="INDEPENDENCIA" maskId={`${uid}r${copy}`} />
      <Tassel x={103} y={7} color={GREEN} />
      <Tassel x={211} y={7} color={WHITE} />
      <Tassel x={312} y={7} color={RED} />
    </g>
  );
}

export default function PapelPicadoSvg() {
  const uid = useId().replace(/:/g, '');
  const width = TILE * COPIES;

  return (
    <svg
      viewBox={`0 0 ${width} 108`}
      preserveAspectRatio="xMinYMin slice"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        <filter id={`patrioPaper${uid}`} x="-4%" y="-8%" width="108%" height="122%">
          <feDropShadow dx="0" dy="1.8" stdDeviation="1.2" floodColor="#331D40" floodOpacity="0.32" />
        </filter>
      </defs>
      <line x1="0" y1="5.4" x2={width} y2="5.4" stroke={CORD} strokeWidth="1.9" strokeLinecap="round" />
      <line x1="0" y1="9" x2={width} y2="9" stroke={CORD} strokeWidth="1.25" strokeLinecap="round" />
      <g filter={`url(#patrioPaper${uid})`}>
        {Array.from({ length: COPIES }, (_, i) => (
          <Tile key={i} x={i * TILE} uid={uid} copy={i} />
        ))}
      </g>
    </svg>
  );
}
